import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../common/Modal'
import { labelColor } from '../../../utils/taskStatusLabel'
import { format } from '../../../utils/date'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface TargetList {
  member_id: number
  member_name: string
  date_list: {
    date: string
    action_needed: boolean
  }[]
}

interface UpdateParam {
  comment: string
  target_list: TargetList[]
}

interface Props {
  targetList: TargetList[]
  closeCallback: VoidFunction
  updateCallback: (param: UpdateParam, callback: VoidFunction) => void
  isDisplay: boolean
  closeable?: boolean
  isApplication?: boolean
}

const MultiApprovedModal: FC<Props> = (props) => {
  const {
    targetList,
    closeCallback,
    updateCallback,
    isDisplay,
    closeable,
    isApplication,
  } = props

  const [comment, setComment] = useState<string>("")
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)

  const [isWaiting, setIsWaiting] = useState<boolean>(false)

  const approvalTargetList = useMemo(() => {
    if (isWaiting) {
      return targetList.map((v) => ({ ...v, date_list: v.date_list.filter((w) => w.action_needed) })).filter((v) => v.date_list.length)
    }
    return targetList
  }, [targetList, isWaiting])

  const displayWord = useMemo(() => {
    if (isApplication) return { text: "申請", status: "processing" }
    return { text: "承認", status: "approved" }
  }, [isApplication])

  const closeModal = () => {
    closeCallback()
    setComment("")
    setIsWaiting(false)
  }

  const submitApproval = () => {
    setIsSubmitLoading(true)
    const param = {
      comment: comment,
      target_list: approvalTargetList,
    }
    updateCallback(
      param,
      () => {
        setIsSubmitLoading(false)
        closeModal()
      },
    )
  }

  useEffect(() => {
    if (isDisplay && !isApplication) setIsWaiting(true)
  }, [isDisplay])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="multi_approved_modal w750"
      closeCallback={closeModal}
      closeable={closeable && !isSubmitLoading}
    >
      <>
        <div className="multi_approved_modal_title">
          {`まとめて${displayWord.text}`}
        </div>
        <div className="multi_approved_modal_main">
          <div className="description">
            {`対象日の仕分けをまとめて${displayWord.text}します`}
          </div>
          {
            !isApplication && (
              <div
                style={{
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  padding: "8px",
                  marginBottom: "8px",
                  backgroundColor: "#F3F3F3",
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    marginBottom: "8px",
                  }}
                >
                  承認対象を選択してください
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    cursor: "pointer",
                    padding: "0 4px",
                  }}
                  onClick={() => setIsWaiting(true)}
                  role="none"
                >
                  <FontAwesomeIcon
                    icon={isWaiting ? ['fas', 'check-circle'] : ['far', 'circle']}
                    style={{
                      color: isWaiting ? "#0f9655" : "#666666",
                    }}
                  />
                  申請中のうち、あなたの承認対象のみ承認する
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    cursor: "pointer",
                    padding: "0 4px",
                  }}
                  onClick={() => setIsWaiting(false)}
                  role="none"
                >
                  <FontAwesomeIcon
                    icon={!isWaiting ? ['fas', 'check-circle'] : ['far', 'circle']}
                    style={{
                      color: !isWaiting ? "#0f9655" : "#666666",
                    }}
                  />
                  申請中全てを承認する
                </div>
              </div>
            )
          }
          {
            approvalTargetList.length ? (
              <>
                <div className="target_display_area">
                  {
                    approvalTargetList.map((v) => (
                      <React.Fragment key={`target_${v.member_id}`}>
                        <div
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "4px",
                          }}
                        >
                          <div
                            style={{
                              padding: "8px 4px",
                            }}
                          >
                            {v.member_name}
                          </div>
                          <div>
                            <div className="target_display_area_title">
                              対象日
                            </div>
                            <div className="target_display_area_main">
                              {
                                v.date_list.map((w) => (
                                  <div className="date">
                                    {format(w.date, "YYYY年M月D日")}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    ))
                  }
                </div>
                <div
                  className="application_area_title"
                  style={{
                    backgroundColor: labelColor(displayWord.status),
                  }}
                >
                  コメント
                </div>
                <textarea
                  placeholder="自由入力欄"
                  className="application_area_text"
                  onBlur={(e) => setComment(e.target.value)}
                />
              </>
            ) : (
              <>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: "bold",
                    color: "#999999",
                    padding: "8px",
                  }}
                >
                  {`${displayWord.text}できるユーザーがいません`}
                </div>
              </>
            )
          }
          <div className="button_area">
            <button
              type="button"
              onClick={closeModal}
              disabled={isSubmitLoading}
            >
              キャンセル
            </button>
            <button
              type="button"
              style={{
                border: `solid 1px ${labelColor(!approvalTargetList.length ? "" : displayWord.status)}`,
                backgroundColor: labelColor(!approvalTargetList.length ? "" : displayWord.status),
                color: "#FFFFFF",
              }}
              onClick={() => submitApproval()}
              disabled={isSubmitLoading || !approvalTargetList.length}
            >
              {
                isSubmitLoading ? (
                  <>
                    <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                  </>
                ) : (
                  <>
                    {displayWord.text}
                  </>
                )
              }
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}

MultiApprovedModal.defaultProps = {
  closeable: true,
  isApplication: false,
}

export default MultiApprovedModal
